import React, { useState } from 'react'
import BasicModal from '../BasicModal'
import ActionButtons from '../../Buttons/ActionButtons'
import { useAppSnackbar } from '@hooks/useAppSnackbar'

import { useTranslation } from 'react-i18next'
import { Box, Button, TextField, useMediaQuery, useTheme } from '@mui/material'
import { TextButtonVariant } from '../../Buttons/TextButton'
import { playlistApi } from '@api/swagger'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { validateEmail, validateEmailText } from '@helpers/validateEmail'
import useEmailMessage from '@hooks/useEmailMessage'
import SharedUsersList from '@components/Playlist/SharedUsersList'

export interface ManagePlaylistSharingModalProps {
  isOpen: boolean
  onClose: () => void
  playlistKey: number
}

const ManagePlaylistSharingModal: React.FC<ManagePlaylistSharingModalProps> = ({
  isOpen,
  onClose,
  playlistKey,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const {
    Playlist: { sharePlaylist: sharePlaylistId },
  } = useLoadingIds()
  const { validationMessages } = useEmailMessage()
  const { handleMessage, handleError } = useAppSnackbar()
  const [email, setEmail] = useState<string>('')
  const [emailError, setEmailError] = useState<string | null>(null)

  const resetEmail = () => setEmail('')
  const resetErrors = () => setEmailError(() => null)
  const resetModal = () => {
    resetEmail()
    resetErrors()
    document.body.focus()
  }
  const isModalFullScreen = useMediaQuery('(max-width:425px)')

  const validateSharePlaylistEmail = (email: string) => {
    const resultMsg = validateEmailText(email)
    setEmailError(validationMessages(resultMsg))
    resultMsg === ''
  }

  const sharePlaylist = async () => {
    if (!validateEmail(email)) {
      validateSharePlaylistEmail(email)
      return
    } else {
      resetEmail()
    }

    try {
      await playlistApi.sharePlaylist({
        body: { recipientEmail: email },
        playlistKey,
      })

      handleMessage(
        t(
          'ManagePlaylistSharingModal.SharePlaylist.Success',
          'The playlist was shared successfully.'
        )
      )
      resetEmail()
      setEmailError(null)
    } catch (error) {
      await handleError(
        error,
        t(
          'ManagePlaylistSharingModal.SharePlaylist.Error',
          'An error occurred while sharing the playlist.'
        )
      )
    }
  }

  const fetchSharedUsersRef = React.useRef<(() => void) | null>(null)

  const { triggerFetch: sharePlaylistToPeople, isLoading } = useLoadingContext({
    asyncFunction: sharePlaylist,
    loadingId: sharePlaylistId,
    callback: () => fetchSharedUsersRef.current?.(),
  })

  const bindFetch = React.useCallback((fetcher: () => void) => {
    fetchSharedUsersRef.current = fetcher
  }, [])

  const modalForm = (
    <Box
      sx={{
        overflowY: 'hidden',
        width: theme.spacing(50),
        maxWidth: { xs: '100%', sm: theme.spacing(60) },
      }}
    >
      <Box
        mt={theme.spacing(4)}
        width="100%"
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1}
        sx={{ maxHeight: 100 }}
        mb={theme.spacing(5)}
      >
        <TextField
          id="sharePlaylistEmail"
          name="sharePlaylistEmail"
          label={t(
            'ManagePlaylistSharingModal.Field.email',
            'Email address to share this playlist'
          )}
          type="email"
          variant="filled"
          value={email}
          fullWidth
          required
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
        />
        <Button
          color={'success'}
          variant="contained"
          size="large"
          onClick={sharePlaylistToPeople}
          disabled={isLoading}
        >
          {t('ManagePlaylistSharingModal.Button.Share', 'Share')}
        </Button>
      </Box>
      <SharedUsersList playlistKey={playlistKey} bindFetch={bindFetch} />
    </Box>
  )

  const modalProps = {
    dialogTitle: t('ManagePlaylistSharingModal.Title', 'Manage Playlist Share'),
    handleFormSubmit: () => null,
    ariaLabel: 'Manage Playlist Share Modal',
    dialogContent: modalForm,
    fullWidth: false,
    afterClose: resetModal,
    dialogActions: (
      <ActionButtons
        tertiaryButtonLabel={TextButtonVariant.Close}
        tertiaryClick={onClose}
        fullWidthButtons={false}
      />
    ),
  }

  return (
    <BasicModal
      key={playlistKey}
      isOpen={isOpen}
      maxWidth="lg"
      {...modalProps}
      noValidate
      fullScreen={isModalFullScreen}
      disableSpacingBody={isModalFullScreen}
    />
  )
}

export default ManagePlaylistSharingModal
